import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, UploadCloud, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle, XCircle, Pause, Play, Square, RotateCcw, ArrowLeft, ArrowRight } from 'lucide-react';
import styles from './Warehouse.module.css';

/**
 * Bulk upload of warehouse products from Excel / CSV.
 *   1 File     drop or choose; download a template
 *   2 Check    columns found by their names (can be changed); every row checked; rows with problems are left out
 *   3 Upload   in parts (100 / 200 / 500 rows), with progress, pause / resume / stop, two retries per part
 *   4 Result   what happened to each row; download the report or the failed rows to fix
 */
const FIELDS = [
    { key: 'name', label: 'Product name', required: true, alias: ['product name', 'product', 'name', 'item', 'item name', 'product name (required)'] },
    { key: 'sku', label: 'SKU', alias: ['sku', 'sku code', 'code', 'item code', 'sku (optional)'] },
    { key: 'brand', label: 'Brand', alias: ['brand', 'company', 'make'] },
    { key: 'category', label: 'Category', alias: ['category', 'group', 'type'] },
    { key: 'hsn', label: 'HSN', alias: ['hsn', 'hsn code', 'hsn/sac'] },
    { key: 'measureUnit', label: 'Unit', alias: ['unit', 'measure unit', 'uom', 'units'] },
    { key: 'size', label: 'Size', alias: ['size', 'volume', 'weight', 'pack size'] },
    { key: 'usageType', label: 'Usage', alias: ['usage', 'usage type', 'use'] },
    { key: 'purchasePrice', label: 'Cost', num: true, alias: ['cost', 'purchase price', 'purchase', 'cost price', 'buying price'] },
    { key: 'transferPrice', label: 'Price to branch', num: true, alias: ['price to branch', 'transfer price', 'branch price', 'franchise price', 'dealer price', 'selling price'] },
    { key: 'mrp', label: 'MRP', num: true, alias: ['mrp', 'm.r.p', 'retail price'] },
    { key: 'gstPercent', label: 'GST %', num: true, alias: ['gst', 'gst %', 'gst rate', 'tax', 'tax %', 'gst%'] },
    { key: 'minStock', label: 'Alert below', num: true, alias: ['alert below', 'min stock', 'minimum', 'min alert', 'reorder level'] },
    { key: 'addStock', label: 'Add stock', num: true, alias: ['add stock', 'stock', 'qty', 'quantity', 'opening stock', 'initial stock'] },
];
const UNITS = { piece: 'piece', pc: 'piece', pcs: 'piece', pieces: 'piece', nos: 'piece', unit: 'piece', ml: 'ml', gm: 'gm', g: 'gm', gram: 'gm', grams: 'gm', liter: 'liter', litre: 'liter', l: 'liter', ltr: 'liter', kg: 'kg', kgs: 'kg' };
const USAGES = ['RETAIL', 'PROFESSIONAL', 'HYBRID', 'CONSUMABLE', 'SAMPLE'];
const GSTS = [0, 5, 12, 18, 28];
const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/** headers in the file -> which column holds each field */
const guessMap = (headers) => {
    const map = {};
    FIELDS.forEach((f) => {
        const i = headers.findIndex((h) => f.alias.includes(norm(h)));
        if (i >= 0 && !Object.values(map).includes(i)) map[f.key] = i;
    });
    return map;
};

/** one row of the sheet -> clean values + problems */
const checkRow = (cells, map, rowNo) => {
    const get = (k) => (map[k] === undefined ? '' : cells[map[k]]);
    const row = { rowNo };
    const errors = []; const warnings = [];
    FIELDS.forEach((f) => {
        const v = get(f.key);
        if (f.num) {
            if (v === '' || v === null || v === undefined) { row[f.key] = ''; return; }
            const n = Number(String(v).replace(/[₹,\s]/g, ''));
            if (!Number.isFinite(n) || n < 0) errors.push(`${f.label} "${v}" is not a number`); else row[f.key] = n;
        } else row[f.key] = String(v ?? '').trim();
    });
    if (!row.name) errors.push('Product name is missing');
    if (row.measureUnit) { const u = UNITS[norm(row.measureUnit)]; if (u) row.measureUnit = u; else { warnings.push(`Unit "${row.measureUnit}" not known, piece used`); row.measureUnit = 'piece'; } }
    if (row.usageType) { const u = String(row.usageType).toUpperCase(); if (USAGES.includes(u)) row.usageType = u; else { warnings.push(`Usage "${row.usageType}" not known, retail used`); row.usageType = 'RETAIL'; } }
    if (row.gstPercent !== '' && !GSTS.includes(row.gstPercent)) errors.push(`GST ${row.gstPercent}% is not 0, 5, 12, 18 or 28`);
    if (row.addStock !== '' && !Number.isInteger(row.addStock)) errors.push('Add stock must be a whole number');
    if (row.transferPrice !== '' && row.purchasePrice !== '' && row.transferPrice < row.purchasePrice) warnings.push('Price to branch is below cost');
    if (!row.sku) warnings.push('No SKU: one will be made');
    if (row.sku) row.sku = row.sku.toUpperCase();
    return { row, errors, warnings };
};

const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const head = FIELDS.map((f) => f.label);
    const ws = XLSX.utils.aoa_to_sheet([head,
        ['Keratin Shampoo 500ml', 'KSH-500', "L'Oreal", 'Hair care', '3305', 'ml', 500, 'RETAIL', 310, 400, 650, 18, 5, 24],
        ['O3+ Bridal Kit', 'O3-BK', 'O3+', 'Skin care', '3304', 'piece', 1, 'PROFESSIONAL', 900, 1200, 1800, 18, 2, 6],
    ]);
    ws['!cols'] = head.map((h) => ({ wch: Math.max(12, h.length + 4) }));
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    const help = XLSX.utils.aoa_to_sheet([
        ['Column', 'What to put'], ['Product name', 'Needed'], ['SKU', 'Optional. Links the product to the same product in each branch. Left empty: one is made (WH-XXXXXX)'],
        ['Unit', 'piece, ml, gm, liter or kg'], ['Size', 'e.g. 500 for a 500 ml bottle'], ['Usage', 'RETAIL, PROFESSIONAL, HYBRID, CONSUMABLE or SAMPLE'],
        ['Cost', 'What HQ pays'], ['Price to branch', 'What the branch is billed'], ['GST %', '0, 5, 12, 18 or 28'],
        ['Alert below', 'Low stock alert'], ['Add stock', 'Units to add now (whole number)'],
        ['Already in the warehouse', 'Matched by SKU, else by name. Choose on the screen to skip them or update their details.'],
    ]);
    help['!cols'] = [{ wch: 26 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, help, 'How to fill');
    XLSX.writeFile(wb, 'warehouse-products-template.xlsx');
};

export default function BulkUpload({ api, headers, warehouse, onClose, onDone }) {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [sheet, setSheet] = useState({ headers: [], rows: [] });
    const [map, setMap] = useState({});
    const [mode, setMode] = useState('skip');
    const [size, setSize] = useState(200);
    const [show, setShow] = useState('all');
    const [drag, setDrag] = useState(false);
    const [readErr, setReadErr] = useState('');
    const [prog, setProg] = useState({ done: 0, parts: 0, part: 0, created: 0, updated: 0, skipped: 0, failed: 0, retry: 0 });
    const [state, setState] = useState('idle');          // idle | running | paused | stopped | done | error
    const [results, setResults] = useState([]);
    const [runErr, setRunErr] = useState('');
    const ctl = useRef({ pause: false, stop: false });
    const input = useRef(null);

    /* ---------- read the file ---------- */
    const read = async (f) => {
        setReadErr('');
        if (!f) return;
        if (!/\.(xlsx|xls|csv)$/i.test(f.name)) { setReadErr('Choose an .xlsx, .xls or .csv file.'); return; }
        if (f.size > 15 * 1024 * 1024) { setReadErr('The file is larger than 15 MB.'); return; }
        try {
            const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false, raw: true });
            const hi = aoa.findIndex((r) => r.some((c) => FIELDS[0].alias.includes(norm(c))));
            const start = hi >= 0 ? hi : 0;
            const head = (aoa[start] || []).map((h) => String(h));
            const rows = aoa.slice(start + 1).map((cells, i) => ({ cells, rowNo: start + i + 2 })).filter((r) => r.cells.some((c) => String(c).trim() !== ''));
            if (!rows.length) { setReadErr('No product rows found in the first sheet.'); return; }
            setFile(f); setSheet({ headers: head, rows }); setMap(guessMap(head)); setStep(2);
        } catch (e) { setReadErr(`Could not read the file: ${e.message}`); }
    };

    /* ---------- check every row ---------- */
    const checked = useMemo(() => {
        const out = sheet.rows.map((r) => checkRow(r.cells, map, r.rowNo));
        const seen = new Map();
        out.forEach((c) => {
            if (!c.row.name) return;
            const key = c.row.sku || `n:${norm(c.row.name)}`;
            if (seen.has(key)) c.errors.push(`Same product as row ${seen.get(key)}`); else seen.set(key, c.row.rowNo);
        });
        return out;
    }, [sheet, map]);
    const good = checked.filter((c) => !c.errors.length);
    const bad = checked.filter((c) => c.errors.length);
    const warned = good.filter((c) => c.warnings.length);
    const visible = checked.filter((c) => (show === 'errors' ? c.errors.length : show === 'warnings' ? c.warnings.length && !c.errors.length : true)).slice(0, 300);

    /* ---------- upload in parts ---------- */
    const send = async (rows, attempt = 0) => {
        try {
            const res = await fetch(`${api}/items/bulk`, { method: 'POST', headers, body: JSON.stringify({ warehouseId: warehouse._id, rows, mode }) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) {
                if (res.status >= 500 && attempt < 2) throw new Error('retry');
                throw Object.assign(new Error(j.message || `Error ${res.status}`), { final: true });
            }
            return j.data;
        } catch (e) {
            if (e.final || attempt >= 2) throw e;
            setProg((p) => ({ ...p, retry: p.retry + 1 }));
            await sleep(1500 * (attempt + 1));
            return send(rows, attempt + 1);
        }
    };
    const run = async (from = 0) => {
        const rows = good.map((c) => c.row);
        const parts = Math.ceil(rows.length / size);
        ctl.current = { pause: false, stop: false };
        setState('running'); setRunErr('');
        if (from === 0) { setResults([]); setProg({ done: 0, parts, part: 0, created: 0, updated: 0, skipped: 0, failed: 0, retry: 0 }); }
        for (let i = from; i < parts; i += 1) {
            if (ctl.current.stop) { setState('stopped'); return; }
            if (ctl.current.pause) { setState('paused'); setProg((p) => ({ ...p, part: i })); return; }
            const chunk = rows.slice(i * size, (i + 1) * size);
            try {
                // eslint-disable-next-line no-await-in-loop
                const data = await send(chunk);
                setResults((r) => [...r, ...data.results]);
                setProg((p) => ({ ...p, part: i + 1, done: p.done + chunk.length, created: p.created + data.summary.created, updated: p.updated + data.summary.updated, skipped: p.skipped + data.summary.skipped, failed: p.failed + data.summary.failed }));
            } catch (e) {
                setRunErr(`Part ${i + 1} of ${parts} could not be uploaded: ${e.message === 'Failed to fetch' ? 'no connection' : e.message}. The parts before it are saved.`);
                setProg((p) => ({ ...p, part: i }));
                setState('error');
                return;
            }
        }
        setState('done'); setStep(4);
    };

    /* ---------- reports ---------- */
    const byRow = new Map(checked.map((c) => [c.row.rowNo, c.row]));
    const report = [
        ...results.map((r) => ({ Row: r.rowNo, Product: byRow.get(r.rowNo)?.name || '', SKU: r.sku || byRow.get(r.rowNo)?.sku || '', Result: r.status, Message: r.message })),
        ...bad.map((c) => ({ Row: c.row.rowNo, Product: c.row.name, SKU: c.row.sku, Result: 'not sent', Message: c.errors.join('; ') })),
    ].sort((a, b) => a.Row - b.Row);
    const downloadReport = () => { const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report), 'Report'); XLSX.writeFile(wb, `warehouse-upload-report-${new Date().toISOString().slice(0, 10)}.xlsx`); };
    const downloadFailed = () => {
        const failedRows = new Set([...results.filter((r) => r.status === 'failed').map((r) => r.rowNo), ...bad.map((c) => c.row.rowNo)]);
        const out = sheet.rows.filter((r) => failedRows.has(r.rowNo)).map((r) => {
            const why = report.find((x) => x.Row === r.rowNo)?.Message || '';
            return [...FIELDS.map((f) => (map[f.key] === undefined ? '' : r.cells[map[f.key]])), why];
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[...FIELDS.map((f) => f.label), 'Problem'], ...out]), 'Fix these');
        XLSX.writeFile(wb, 'warehouse-products-to-fix.xlsx');
    };

    const pct = prog.parts ? Math.round((prog.part / prog.parts) * 100) : 0;
    const busy = state === 'running';
    const close = () => { if (busy && !window.confirm('Stop the upload? Parts already sent stay saved.')) return; ctl.current.stop = true; if (results.length) onDone?.(); onClose(); };

    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-label="Bulk upload products">
                <div className={styles.modalHead}>
                    <div><h3>Bulk upload products</h3><p>Into {warehouse.name} · Excel or CSV</p></div>
                    <button type="button" className={styles.iconBtn} onClick={close} aria-label="Close"><X size={16} /></button>
                </div>
                <ol className={styles.steps4}>
                    {['File', 'Check', 'Upload', 'Result'].map((l, i) => <li key={l} className={step === i + 1 ? styles.stepNow : step > i + 1 ? styles.stepDone : ''}><b>{i + 1}</b> {l}</li>)}
                </ol>

                <div className={styles.modalBody}>
                    {step === 1 && (
                        <>
                            <div
                                className={`${styles.drop} ${drag ? styles.dropOn : ''}`}
                                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                                onDragLeave={() => setDrag(false)}
                                onDrop={(e) => { e.preventDefault(); setDrag(false); read(e.dataTransfer.files?.[0]); }}
                                onClick={() => input.current?.click()}
                                role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
                            >
                                <UploadCloud size={34} />
                                <b>Drop the Excel or CSV file here</b>
                                <span>or click to choose · .xlsx, .xls, .csv · up to 15 MB</span>
                                <input ref={input} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => read(e.target.files?.[0])} />
                            </div>
                            {readErr && <p className={styles.err}>{readErr}</p>}
                            <div className={styles.tip}>
                                <FileSpreadsheet size={16} />
                                <span>Use the template so every column is found. Only <b>Product name</b> is needed; products without a SKU get one. Thousands of rows are fine: they go up in parts.</span>
                                <button type="button" className={styles.ghostBtn} onClick={downloadTemplate}><Download size={13} /> Template</button>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className={styles.fileBar}><FileSpreadsheet size={15} /> <b>{file?.name}</b> · {sheet.rows.length} rows <button type="button" className={styles.linkBtn} onClick={() => { setStep(1); setFile(null); }}>Change file</button></div>
                            <div className={styles.mapGrid} aria-label="Columns">
                                {FIELDS.map((f) => (
                                    <label key={f.key} className={`${styles.mapItem} ${map[f.key] === undefined ? (f.required ? styles.mapMissing : styles.mapNone) : ''}`}>
                                        <span>{f.label}{f.required ? ' *' : ''}</span>
                                        <select value={map[f.key] ?? ''} onChange={(e) => { const v = e.target.value; const next = { ...map }; if (v === '') delete next[f.key]; else { Object.keys(next).forEach((k) => { if (next[k] === Number(v)) delete next[k]; }); next[f.key] = Number(v); } setMap(next); }}>
                                            <option value="">— not in the file —</option>
                                            {sheet.headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                                        </select>
                                    </label>
                                ))}
                            </div>
                            <div className={styles.checkBar}>
                                <span className={styles.okTxt}><CheckCircle2 size={14} /> {good.length} ready</span>
                                {warned.length > 0 && <span className={styles.warnTxt}><AlertTriangle size={14} /> {warned.length} with a note</span>}
                                {bad.length > 0 && <span className={styles.badTxt}><XCircle size={14} /> {bad.length} with problems (left out)</span>}
                                <div className={styles.chips} style={{ flex: 'none', marginLeft: 'auto' }}>
                                    {[['all', 'All'], ['errors', 'Problems'], ['warnings', 'Notes']].map(([v, l]) => <button type="button" key={v} className={show === v ? styles.chipOn : ''} onClick={() => setShow(v)}>{l}</button>)}
                                </div>
                            </div>
                            <div className={styles.tableWrap} style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
                                <table className={styles.table}>
                                    <thead><tr><th>Row</th><th>Product</th><th>SKU</th><th>Unit</th><th className={styles.num}>Cost</th><th className={styles.num}>To branch</th><th className={styles.num}>GST</th><th className={styles.num}>Add stock</th><th>Check</th></tr></thead>
                                    <tbody>
                                        {visible.map((c) => (
                                            <tr key={c.row.rowNo} className={c.errors.length ? styles.rowBad : ''}>
                                                <td>{c.row.rowNo}</td><td><b>{c.row.name || '—'}</b></td><td>{c.row.sku || '—'}</td><td>{c.row.measureUnit || 'piece'}{c.row.size ? ` · ${c.row.size}` : ''}</td>
                                                <td className={styles.num}>{c.row.purchasePrice}</td><td className={styles.num}>{c.row.transferPrice}</td><td className={styles.num}>{c.row.gstPercent === '' ? '18' : c.row.gstPercent}%</td><td className={styles.num}>{c.row.addStock}</td>
                                                <td>{c.errors.length ? <small className={styles.subBad}>{c.errors.join('; ')}</small> : c.warnings.length ? <small className={styles.warnTxt}>{c.warnings.join('; ')}</small> : <small className={styles.okTxt}>OK</small>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {visible.length >= 300 && <small className={styles.help}>Showing the first 300 of these rows; all {checked.length} rows are checked.</small>}
                            <div className={styles.grid2}>
                                <div className={styles.field}><span>Products already in the warehouse (same SKU, else same name)</span>
                                    <div className={styles.seg} role="radiogroup" aria-label="Existing products">
                                        <button type="button" role="radio" aria-checked={mode === 'skip'} className={mode === 'skip' ? styles.segOn : ''} onClick={() => setMode('skip')}>Keep as they are</button>
                                        <button type="button" role="radio" aria-checked={mode === 'update'} className={mode === 'update' ? styles.segOn : ''} onClick={() => setMode('update')}>Update their details</button>
                                    </div>
                                    <small className={styles.help}>"Add stock" is added either way.</small>
                                </div>
                                <label className={styles.field}><span>Rows per part</span>
                                    <select value={size} onChange={(e) => setSize(Number(e.target.value))}>{[100, 200, 500].map((n) => <option key={n} value={n}>{n} rows ({Math.max(1, Math.ceil(good.length / n))} part{Math.ceil(good.length / n) === 1 ? '' : 's'})</option>)}</select>
                                </label>
                            </div>
                        </>
                    )}

                    {step === 3 && (
                        <div className={styles.progressBox}>
                            <div className={styles.progHead}>
                                <b>{state === 'running' ? `Uploading part ${Math.min(prog.part + 1, prog.parts)} of ${prog.parts}…` : state === 'paused' ? 'Paused' : state === 'stopped' ? 'Stopped' : state === 'error' ? 'Stopped by a problem' : 'Ready'}</b>
                                <span>{prog.done} of {good.length} rows · {pct}%</span>
                            </div>
                            <div className={styles.progBar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>
                            <div className={styles.progCounts}>
                                <span className={styles.okTxt}>{prog.created} added</span><span>{prog.updated} updated</span><span>{prog.skipped} kept as they were</span><span className={prog.failed ? styles.badTxt : ''}>{prog.failed} failed</span>{prog.retry > 0 && <span className={styles.warnTxt}>{prog.retry} retr{prog.retry === 1 ? 'y' : 'ies'}</span>}
                            </div>
                            {runErr && <p className={styles.err}>{runErr}</p>}
                        </div>
                    )}

                    {step === 4 && (
                        <>
                            <div className={styles.cards}>
                                <div className={styles.card}><small>Added</small><b className={styles.good}>{prog.created}</b></div>
                                <div className={styles.card}><small>Updated</small><b>{prog.updated}</b></div>
                                <div className={styles.card}><small>Kept as they were</small><b>{prog.skipped}</b></div>
                                <div className={styles.card}><small>Failed</small><b className={prog.failed ? styles.bad : ''}>{prog.failed}</b></div>
                                <div className={styles.card}><small>Left out (problems)</small><b className={bad.length ? styles.bad : ''}>{bad.length}</b></div>
                            </div>
                            <div className={styles.tableWrap} style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
                                <table className={styles.table}>
                                    <thead><tr><th>Row</th><th>Product</th><th>Result</th><th>Message</th></tr></thead>
                                    <tbody>{report.slice(0, 500).map((r) => (
                                        <tr key={`${r.Row}-${r.Result}`} className={['failed', 'not sent'].includes(r.Result) ? styles.rowBad : ''}>
                                            <td>{r.Row}</td><td>{r.Product}</td><td><span className={`${styles.pill} ${styles[r.Result === 'created' ? 'pillGood' : ['failed', 'not sent'].includes(r.Result) ? 'pillBad' : 'pill']}`}>{r.Result}</span></td><td>{r.Message}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.modalFoot}>
                    {step === 2 && <>
                        <button type="button" className={styles.ghostBtn} onClick={() => setStep(1)}><ArrowLeft size={13} /> Back</button>
                        <button type="button" className={styles.primaryBtn} disabled={!good.length || map.name === undefined} onClick={() => { setStep(3); run(0); }}>Upload {good.length} product{good.length === 1 ? '' : 's'} <ArrowRight size={13} /></button>
                    </>}
                    {step === 3 && <>
                        {busy && <button type="button" className={styles.ghostBtn} onClick={() => { ctl.current.pause = true; }}><Pause size={13} /> Pause</button>}
                        {busy && <button type="button" className={styles.ghostBtn} onClick={() => { ctl.current.stop = true; }}><Square size={13} /> Stop</button>}
                        {state === 'paused' && <button type="button" className={styles.primaryBtn} onClick={() => run(prog.part)}><Play size={13} /> Resume</button>}
                        {state === 'error' && <button type="button" className={styles.primaryBtn} onClick={() => run(prog.part)}><RotateCcw size={13} /> Try this part again</button>}
                        {['stopped', 'error', 'paused'].includes(state) && <button type="button" className={styles.ghostBtn} onClick={() => setStep(4)}>See what was saved</button>}
                        {busy && <Loader2 size={16} className={styles.spin} />}
                    </>}
                    {step === 4 && <>
                        <button type="button" className={styles.ghostBtn} onClick={downloadReport}><Download size={13} /> Report</button>
                        {(prog.failed > 0 || bad.length > 0) && <button type="button" className={styles.ghostBtn} onClick={downloadFailed}><Download size={13} /> Rows to fix</button>}
                        <button type="button" className={styles.primaryBtn} onClick={() => { onDone?.(); onClose(); }}>Done</button>
                    </>}
                </div>
            </div>
        </div>
    );
}
